import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Grid3x3, Plus, Pencil, X, Trash2, Move, History, ArrowRight, AlertTriangle,
  Save, DollarSign, ChevronRight, ChevronDown, UserMinus2, Search, Users as UsersIcon,
} from "lucide-react";

const fmt = (n, currency = "USD") => {
  if (n == null) return "—";
  const symbol = { USD: "$", BRL: "R$", EUR: "€" }[currency] || currency + " ";
  return `${symbol}${Number(n).toLocaleString(currency === "BRL" ? "pt-BR" : "en-US", { maximumFractionDigits: 0 })}`;
};

export default function SalaryMatrix() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [matrices, setMatrices] = useState([]);
  const [matrixId, setMatrixId] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(null); // { user, currentCellId }
  const [historyFor, setHistoryFor] = useState(null);
  const [error, setError] = useState("");
  const [newMatrixModal, setNewMatrixModal] = useState(false);
  const [editMetaFor, setEditMetaFor] = useState(null);
  const [cellFilterId, setCellFilterId] = useState(null); // Iter 47d — filter EmployeeAllocationList by clicked cell

  const loadMatrices = async () => {
    try {
      const r = await api.get("/salary-matrices");
      setMatrices(r.data || []);
      // Initial selection: keep current if still exists; otherwise first
      const currentStillExists = r.data.some((m) => m.id === matrixId);
      if (!currentStillExists) setMatrixId(r.data[0]?.id || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Cannot load matrices");
    }
  };
  const load = async () => {
    if (!matrixId) return;
    try {
      const r = await api.get(`/salary-matrix?matrix_id=${matrixId}`);
      setMatrix(r.data);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Cannot load matrix");
    }
  };
  useEffect(() => { loadMatrices(); }, []);
  useEffect(() => { load(); }, [matrixId]); // eslint-disable-line

  if (!["hr", "admin", "ceo"].includes(user?.role)) {
    return (
      <div className="sw-card text-center max-w-md mx-auto mt-12">
        <AlertTriangle size={28} className="text-amber-500 mx-auto mb-3" />
        <h2 className="sw-heading text-[18px]">Restricted area</h2>
        <p className="text-[13px] text-[#747474] mt-2">{t("sal.access_denied")}</p>
      </div>
    );
  }

  const canEdit = ["hr", "admin"].includes(user?.role);

  if (error) return <div className="sw-card text-rose-700 bg-rose-50 border-rose-200">{error}</div>;
  if (!matrix) return <div className="text-[13px] text-[#94a3b8]">Loading matrix…</div>;

  const { tracks = [], levels = [], cells = [], unplaced_users = [] } = matrix;
  const cellAt = (tid, lid) => cells.find((c) => c.track_id === tid && c.level_id === lid);
  const sortedTracks = [...tracks].sort((a, b) => (a.order || 0) - (b.order || 0));
  const sortedLevels = [...levels].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><Grid3x3 size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("sal.title")}</h1>
            <p className="text-[14px] text-[#747474]">
              {matrix?.name || "Tracks × Levels"} {matrix?.currency ? `· ${matrix.currency}` : ""}
              {matrix?.applies_to_all_contract_types ? ` · ${t("sal.applies_all_badge")}` : (matrix?.applies_to_contract_types?.length ? ` · ${matrix.applies_to_contract_types.join(", ")}` : "")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && matrixId && matrixId !== "main" && (
            <button data-testid="edit-matrix-meta-btn" onClick={() => setEditMetaFor(matrices.find((m) => m.id === matrixId))} className="sw-btn-secondary" title={t("sal.edit_meta")}><Pencil size={13} /> Metadados</button>
          )}
          <button data-testid="edit-matrix-btn" onClick={() => setEditing(true)} disabled={!canEdit} className={`sw-btn-primary ${canEdit ? "" : "opacity-50 cursor-not-allowed"}`}><Pencil size={14} />  {t("sal.edit_values")}</button>
        </div>
      </div>

      {/* Matrix picker */}
      <div className="flex items-center gap-2 flex-wrap border-b border-[#eeeded] pb-3" data-testid="matrix-picker">
        {matrices.map((m) => (
          <button
            key={m.id}
            data-testid={`matrix-tab-${m.id}`}
            onClick={() => setMatrixId(m.id)}
            className={`px-3 py-1.5 rounded-[10px] text-[12.5px] transition-all border ${matrixId === m.id ? "bg-[#475569] text-white border-[#475569]" : "bg-white border-[#e2e8f0] text-[#475569] hover:border-[#94a3b8]"}`}
          >
            <span className="font-medium">{m.name}</span>
            <span className={`ml-1.5 text-[10.5px] ${matrixId === m.id ? "opacity-80" : "opacity-60"}`}>
              {m.currency} · {t("sal.people_n", { n: m.occupants_count || 0 })}
              {m.applies_to_all_contract_types ? ` · ${t("sal.all_chip")}` : (m.applies_to_contract_types?.length ? ` · ${m.applies_to_contract_types.join(", ")}` : "")}
            </span>
          </button>
        ))}
        {canEdit && (
          <button
            data-testid="new-matrix-btn"
            onClick={() => setNewMatrixModal(true)}
            className="px-3 py-1.5 rounded-[10px] text-[12.5px] border border-dashed border-[#94a3b8] text-[#475569] hover:bg-[#f8fafc]"
          ><Plus size={12} />  {t("sal.new_matrix")}</button>
        )}
      </div>

      <PendingSalaryRequests onChanged={load} />

      {tracks.length === 0 ? (
        <div className="sw-card text-center py-12">
          <Grid3x3 size={32} className="text-[#94a3b8] mx-auto mb-3" />
          <h3 className="sw-heading text-[18px]">{t("sal.no_matrix")}</h3>
          <p className="text-[13px] text-[#747474] mt-2 max-w-md mx-auto">Define tracks (job functions) and levels (career steps) to start placing employees in salary bands.</p>
          <button onClick={() => setEditing(true)} className="sw-btn-primary mt-4"><Plus size={14} />  {t("sal.create_matrix")}</button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-[#e2e8f0] rounded-[16px] overflow-hidden">
            <thead>
              <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                <th className="p-3 text-left text-[10px] uppercase tracking-[0.12em] font-semibold text-[#475569] sticky left-0 bg-[#f8fafc] z-10 min-w-[180px]">Track</th>
                {sortedLevels.map((l) => (
                  <th key={l.id} data-testid={`level-header-${l.id}`} className="p-3 text-center text-[10px] uppercase tracking-[0.12em] font-semibold text-[#475569] min-w-[220px]">{l.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedTracks.map((t) => (
                <tr key={t.id} className="border-b border-[#f1f5f9] last:border-b-0">
                  <td className="p-3 align-top sticky left-0 bg-white z-10 border-r border-[#e2e8f0]">
                    <div className="text-[13px] font-semibold text-[#1e293b]">{t.name}</div>
                    {t.description && <div className="text-[10px] text-[#94a3b8] mt-0.5">{t.description}</div>}
                  </td>
                  {sortedLevels.map((l) => {
                    const cell = cellAt(t.id, l.id);
                    const isActive = cellFilterId === cell?.id;
                    return (
                      <td
                        key={l.id}
                        data-testid={`cell-${t.id}-${l.id}`}
                        className={`p-2 align-top ${cell ? "cursor-pointer" : ""} ${isActive ? "bg-[#eef2f7]" : ""}`}
                        onClick={() => cell && setCellFilterId(isActive ? null : cell.id)}
                        title={cell ? "Clique para filtrar a lista abaixo por esta célula" : ""}
                      >
                        {cell ? (
                          <CellBox
                            cell={cell}
                            track={t}
                            level={l}
                            currency={matrix?.currency}
                            onMoveUser={(u) => setMoving({ user: u, currentCellId: cell.id })}
                            onHistory={(u) => setHistoryFor(u)}
                          />
                        ) : <div className="rounded-[12px] border border-dashed border-[#e2e8f0] h-[120px]" />}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EmployeeAllocationList
        cellFilterId={cellFilterId}
        matrixId={matrixId}
        currency={matrix?.currency}
        cellsById={Object.fromEntries((cells || []).map((c) => [c.id, c]))}
        tracksById={Object.fromEntries((tracks || []).map((t) => [t.id, t]))}
        levelsById={Object.fromEntries((levels || []).map((l) => [l.id, l]))}
        onClearCellFilter={() => setCellFilterId(null)}
        onMove={(u, cellId) => setMoving({ user: u, currentCellId: cellId })}
        refreshKey={matrix?.updated_at || matrixId}
      />

      {editing && (
        <EditMatrixModal
          initial={matrix}
          matrixId={matrixId}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); loadMatrices(); }}
        />
      )}
      {moving && (
        <MoveEmployeeModal
          target={moving.user}
          currentCellId={moving.currentCellId}
          matrix={matrix}
          matrixId={matrixId}
          onClose={() => setMoving(null)}
          onSaved={() => { setMoving(null); load(); }}
        />
      )}
      {historyFor && (
        <HistoryModal user={historyFor} onClose={() => setHistoryFor(null)} />
      )}
      {newMatrixModal && (
        <MatrixMetaModal
          initial={null}
          onClose={() => setNewMatrixModal(false)}
          onSaved={(created) => { setNewMatrixModal(false); loadMatrices().then(() => setMatrixId(created.id)); }}
        />
      )}
      {editMetaFor && (
        <MatrixMetaModal
          initial={editMetaFor}
          onClose={() => setEditMetaFor(null)}
          onSaved={() => { setEditMetaFor(null); loadMatrices(); }}
          onDeleted={() => { setEditMetaFor(null); setMatrixId(null); loadMatrices(); }}
        />
      )}
    </div>
  );
}

/**
 * Iter 47 — Metadata modal (name, currency, contract types, active flag).
 * Also hosts the "Delete this matrix" action for non-main matrices.
 */
function MatrixMetaModal({ initial, onClose, onSaved, onDeleted }) {
  const { t } = useI18n();
  const isNew = !initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    currency: initial?.currency || "USD",
    description: initial?.description || "",
    applies_to_all_contract_types: !!initial?.applies_to_all_contract_types,
    applies_to_contract_types: initial?.applies_to_contract_types || [],
    active: initial?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const CTs = [
    { code: "CLT_FrankCrum", label: "CLT · FrankCrum (USA)" },
    { code: "PJ_Lummar", label: "PJ · Lummar (BR)" },
    { code: "PJ_Direct", label: "PJ · Direct (BR)" },
    { code: "NONE", label: t("sal.no_contract") },
  ];
  const toggleCT = (code) => {
    if (form.applies_to_contract_types.includes(code)) {
      setForm({ ...form, applies_to_contract_types: form.applies_to_contract_types.filter((x) => x !== code) });
    } else {
      setForm({ ...form, applies_to_contract_types: [...form.applies_to_contract_types, code] });
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      if (isNew) {
        const r = await api.post("/salary-matrices", form);
        onSaved(r.data);
      } else {
        await api.patch(`/salary-matrices/${initial.id}`, form);
        onSaved(initial);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirm(t("sal.delete_matrix_confirm", { name: initial.name }))) return;
    setSaving(true);
    setErr("");
    try {
      await api.delete(`/salary-matrices/${initial.id}`);
      onDeleted?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erro ao deletar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} className="bg-white rounded-[20px] max-w-[540px] w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="matrix-meta-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{isNew ? t("sal.new_matrix_full") : t("sal.edit_meta")}</div>
            <h3 className="sw-heading text-[18px] mt-0.5">{isNew ? t("sal.register") : initial.name}</h3>
          </div>
          <button type="button" onClick={onClose}><X size={20} /></button>
        </div>
        {err && <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-[10px] px-3 py-2 mb-3">{err}</div>}
        <div className="flex flex-col gap-3">
          <div><label className="sw-label">{t("common.name")} *</label><input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" placeholder={t("sal.matrix_name_ph")} data-testid="matrix-meta-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="sw-label">{t("sal.currency")} *</label>
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="sw-input" data-testid="matrix-meta-currency">
                <option value="USD">USD (US$)</option>
                <option value="BRL">BRL (R$)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer text-[12.5px]">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#475569]" />
                {t("sal.active_picker")}
              </label>
            </div>
          </div>
          <div><label className="sw-label">{t("common.description")}</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" /></div>
          <div>
            <label className="sw-label">{t("sal.applicability")}</label>
            <label className="flex items-center gap-2 mb-2 text-[12.5px] cursor-pointer">
              <input type="checkbox" checked={form.applies_to_all_contract_types} onChange={(e) => setForm({ ...form, applies_to_all_contract_types: e.target.checked })} className="w-4 h-4 accent-[#475569]" data-testid="matrix-meta-all" />
              {t("sal.applies_all_1")} <b>{t("sal.all_contract_types")}</b>
            </label>
            <div className={`flex gap-2 flex-wrap ${form.applies_to_all_contract_types ? "opacity-40 pointer-events-none" : ""}`}>
              {CTs.map((ct) => (
                <button type="button" key={ct.code} onClick={() => toggleCT(ct.code)} data-testid={`matrix-meta-ct-${ct.code}`}
                  className={`px-2.5 py-1 rounded-[8px] text-[11.5px] border ${form.applies_to_contract_types.includes(ct.code) ? "bg-[#475569] text-white border-[#475569]" : "bg-white border-[#e2e8f0] text-[#475569]"}`}
                >{ct.label}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center mt-5 gap-2">
          <div>
            {!isNew && initial.id !== "main" && (
              <button type="button" onClick={del} disabled={saving} className="text-[12px] text-[#881a27] hover:bg-[#fef1f3] px-3 py-1.5 rounded-[8px] inline-flex items-center gap-1" data-testid="matrix-meta-delete"><Trash2 size={12} />  {t("sal.delete_matrix")}</button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button type="submit" disabled={saving || !form.name} className="sw-btn-primary" data-testid="matrix-meta-save">
              <Save size={13} /> {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function CellBox({ cell, track, level, onMoveUser, onHistory, currency = "USD" }) {
  const { t } = useI18n();
  const range = cell.salary_min || cell.salary_max ? `${fmt(cell.salary_min, currency)} – ${fmt(cell.salary_max, currency)}` : "—";
  return (
    <div className="rounded-[12px] border border-[#e2e8f0] bg-white p-2.5 min-h-[120px] flex flex-col gap-1.5">
      <div className="text-[10px] text-[#475569] font-medium flex items-center gap-1"><DollarSign size={10} />{range}</div>
      {cell.target_bonus_pct > 0 && <div className="text-[9px] text-[#94a3b8] uppercase tracking-wider">+ {cell.target_bonus_pct}% bonus</div>}
      <div className="flex flex-wrap gap-1 mt-1">
        {(cell.occupants || []).map((u) => {
          const inBand = u.current_salary && cell.salary_min && cell.salary_max
            ? u.current_salary >= cell.salary_min && u.current_salary <= cell.salary_max
            : true;
          return (
            <div
              key={u.id}
              data-testid={`occupant-${cell.id}-${u.id}`}
              className={`group relative inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] cursor-pointer border ${inBand ? "bg-[#475569] text-white border-[#475569]" : "bg-rose-50 text-rose-700 border-rose-200"}`}
              onClick={() => onMoveUser(u)}
              title={`${u.name} · ${u.role} · ${fmt(u.current_salary, currency)}${inBand ? "" : " (out of band!)"}`}
            >
              <span className="truncate max-w-[110px]">{u.name.split(" ").slice(0, 2).join(" ")}</span>
              {!inBand && <AlertTriangle size={10} className="text-rose-500" />}
              <button
                onClick={(e) => { e.stopPropagation(); onHistory(u); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                title={t("sal.view_history")}
              >
                <History size={10} />
              </button>
            </div>
          );
        })}
        {(!cell.occupants || cell.occupants.length === 0) && (
          <span className="text-[10px] text-[#94a3b8] italic">empty</span>
        )}
      </div>
    </div>
  );
}

function EditMatrixModal({ initial, matrixId, onClose, onSaved }) {
  const { t } = useI18n();
  const [tracks, setTracks] = useState(initial.tracks?.length ? initial.tracks : [{ id: null, name: "Engineering", description: "", order: 0 }]);
  const [levels, setLevels] = useState(initial.levels?.length ? initial.levels : [{ id: null, name: "I", order: 0 }]);
  const [cells, setCells] = useState(initial.cells || []);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const ensureCell = (tid, lid) => {
    const found = cells.find((c) => c.track_id === tid && c.level_id === lid);
    if (found) return found;
    const fresh = { track_id: tid, level_id: lid, salary_min: 0, salary_max: 0, target_bonus_pct: 0, notes: "" };
    setCells([...cells, fresh]);
    return fresh;
  };

  const updateCell = (tid, lid, patch) => {
    setCells((prev) => {
      const exists = prev.find((c) => c.track_id === tid && c.level_id === lid);
      if (exists) return prev.map((c) => (c.track_id === tid && c.level_id === lid ? { ...c, ...patch } : c));
      return [...prev, { track_id: tid, level_id: lid, salary_min: 0, salary_max: 0, target_bonus_pct: 0, notes: "", ...patch }];
    });
  };

  const cellOf = (tid, lid) => cells.find((c) => c.track_id === tid && c.level_id === lid) || { salary_min: 0, salary_max: 0, target_bonus_pct: 0 };

  const addTrack = () => setTracks([...tracks, { id: null, name: `Track ${tracks.length + 1}`, description: "", order: tracks.length }]);
  const removeTrack = (i) => setTracks(tracks.filter((_, idx) => idx !== i));
  const addLevel = () => setLevels([...levels, { id: null, name: `L${levels.length + 1}`, order: levels.length }]);
  const removeLevel = (i) => setLevels(levels.filter((_, idx) => idx !== i));

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      // Build only cells that are connected to a current track+level
      const validCells = cells.filter((c) =>
        tracks.find((t) => (t.id || t.name) === c.track_id || t.id === c.track_id) &&
        levels.find((l) => (l.id || l.name) === c.level_id || l.id === c.level_id)
      ).map((c) => ({ ...c, salary_min: parseFloat(c.salary_min) || 0, salary_max: parseFloat(c.salary_max) || 0, target_bonus_pct: parseFloat(c.target_bonus_pct) || 0 }));
      const body = { tracks, levels, cells: validCells };
      await api.put(`/salary-matrix?matrix_id=${encodeURIComponent(matrixId || "main")}`, body);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[20px] max-w-[1100px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="sw-heading text-[20px]">{t("sal.edit_matrix")}</h3>
            <p className="text-[12px] text-[#747474] mt-1">{t("sal.edit_matrix_hint")}</p>
          </div>
          <button onClick={onClose} className="text-[#94a3b8]"><X size={20} /></button>
        </div>

        {err && <div className="rounded-[10px] border border-rose-200 bg-rose-50 text-rose-700 text-[12px] p-3 mb-4">{err}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#475569] mb-2">Tracks (rows)</div>
            <div className="flex flex-col gap-2">
              {tracks.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    data-testid={`track-name-${i}`}
                    value={t.name}
                    onChange={(e) => setTracks(tracks.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    placeholder={t("sal.track_name_ph")}
                    className="sw-input !py-1 !text-[12px] flex-1"
                  />
                  <input
                    value={t.description || ""}
                    onChange={(e) => setTracks(tracks.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                    placeholder="Description"
                    className="sw-input !py-1 !text-[12px] flex-1"
                  />
                  {tracks.length > 1 && <button onClick={() => removeTrack(i)} className="text-rose-500"><X size={14} /></button>}
                </div>
              ))}
            </div>
            <button data-testid="add-track" onClick={addTrack} className="sw-btn-secondary !text-[12px] mt-2"><Plus size={12} />  {t("sal.add_track")}</button>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#475569] mb-2">Levels (columns)</div>
            <div className="flex flex-col gap-2">
              {levels.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    data-testid={`level-name-${i}`}
                    value={l.name}
                    onChange={(e) => setLevels(levels.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    placeholder={t("sal.level_name_ph")}
                    className="sw-input !py-1 !text-[12px] flex-1"
                  />
                  {levels.length > 1 && <button onClick={() => removeLevel(i)} className="text-rose-500"><X size={14} /></button>}
                </div>
              ))}
            </div>
            <button data-testid="add-level" onClick={addLevel} className="sw-btn-secondary !text-[12px] mt-2"><Plus size={12} />  {t("sal.add_level")}</button>
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#475569] mb-2">Salary bands per cell</div>
        <div className="text-[11px] text-[#94a3b8] mb-3">Enter min, max and target bonus % for each intersection. Bands can be edited any time without touching employee placements.</div>
        <div className="overflow-x-auto">
          <table className="min-w-full border border-[#e2e8f0] rounded-[12px] overflow-hidden">
            <thead className="bg-[#f8fafc]">
              <tr>
                <th className="p-2 text-left text-[10px] uppercase tracking-wider text-[#475569]">Track \ Level</th>
                {levels.map((l, i) => <th key={i} className="p-2 text-center text-[10px] uppercase tracking-wider text-[#475569]">{l.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {tracks.map((t, ti) => (
                <tr key={ti} className="border-t border-[#e2e8f0]">
                  <td className="p-2 text-[12px] font-medium text-[#1e293b] bg-[#f8fafc]">{t.name}</td>
                  {levels.map((l, li) => {
                    const tid = t.id || `tmp-t${ti}`;
                    const lid = l.id || `tmp-l${li}`;
                    const c = cellOf(tid, lid);
                    return (
                      <td key={li} className="p-2">
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1">
                            <input
                              data-testid={`cell-min-${ti}-${li}`}
                              type="number"
                              value={c.salary_min}
                              onFocus={() => ensureCell(tid, lid)}
                              onChange={(e) => updateCell(tid, lid, { salary_min: e.target.value })}
                              placeholder={t("sal.min_ph")}
                              className="sw-input !py-1 !text-[11px] w-[85px]"
                            />
                            <input
                              data-testid={`cell-max-${ti}-${li}`}
                              type="number"
                              value={c.salary_max}
                              onChange={(e) => updateCell(tid, lid, { salary_max: e.target.value })}
                              placeholder={t("sal.max_ph")}
                              className="sw-input !py-1 !text-[11px] w-[85px]"
                            />
                          </div>
                          <input
                            type="number"
                            value={c.target_bonus_pct}
                            onChange={(e) => updateCell(tid, lid, { target_bonus_pct: e.target.value })}
                            placeholder="bonus %"
                            className="sw-input !py-1 !text-[11px] w-[176px]"
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-[#e2e8f0]">
          <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button data-testid="save-matrix" onClick={save} disabled={saving} className="sw-btn-primary"><Save size={13} /> {saving ? "Saving…" : "Save matrix"}</button>
        </div>
      </div>
    </div>
  );
}

function MoveEmployeeModal({ target, currentCellId, matrix, matrixId, onClose, onSaved }) {
  const { t } = useI18n();
  const [cellId, setCellId] = useState(currentCellId || "");
  const [salary, setSalary] = useState(target.current_salary || "");
  const [reason, setReason] = useState("");
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [accept, setAccept] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState(null);
  // Iter 47c — allow moving between matrices from within the modal
  const [allMatrices, setAllMatrices] = useState([]);
  const [targetMatrixId, setTargetMatrixId] = useState(matrixId || "main");
  const [targetMatrix, setTargetMatrix] = useState(matrix);

  const cell = targetMatrix?.cells?.find((c) => c.id === cellId);
  const outOfBand = cell && salary && (Number(salary) < (cell.salary_min || 0) || Number(salary) > (cell.salary_max || 0));
  const currency = targetMatrix?.currency || "USD";

  useEffect(() => {
    api.get(`/users/${target.id}/salary-history`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
    api.get("/salary-matrices").then((r) => setAllMatrices(r.data || []));
  }, [target.id]);

  // When user picks a different matrix, fetch its cells/tracks/levels so the
  // dropdown updates and the cell picker reflects the correct bands+currency.
  useEffect(() => {
    if (targetMatrixId === matrixId) {
      setTargetMatrix(matrix);
      return;
    }
    api.get(`/salary-matrix?matrix_id=${encodeURIComponent(targetMatrixId)}`)
      .then((r) => { setTargetMatrix(r.data); setCellId(""); })
      .catch(() => {});
  }, [targetMatrixId, matrix, matrixId]);

  const submit = async (remove = false) => {
    setErr(""); setSaving(true);
    try {
      if (remove) {
        await api.delete(`/users/${target.id}/salary-cell`);
        onSaved();
      } else {
        const r = await api.post(`/users/${target.id}/salary-move`, {
          cell_id: cellId || null,
          matrix_id: targetMatrixId || "main",
          new_salary: parseFloat(salary),
          reason: reason || "Movement",
          effective_date: effective,
          bonus_pct: null,
          accept_out_of_band: accept,
        });
        if (r.data?.queued) {
          alert(t("sal.move_queued", { role: r.data.next_approver_role }));
        }
        onSaved();
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="move-modal" className="bg-white rounded-[20px] max-w-[560px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="sw-heading text-[18px]">Move {target.name}</h3>
            <p className="text-[12px] text-[#747474] mt-1">{target.job_title || target.role}</p>
          </div>
          <button onClick={onClose} className="text-[#94a3b8]"><X size={20} /></button>
        </div>

        {err && <div className="rounded-[10px] border border-rose-200 bg-rose-50 text-rose-700 text-[12px] p-3 mb-3">{err}</div>}

        <div className="flex flex-col gap-3">
          {/* Iter 47c — Matrix selector (allows moving between matrices) */}
          <div>
            <label className="sw-label">{t("sal.title")}</label>
            <select
              data-testid="move-matrix"
              value={targetMatrixId}
              onChange={(e) => setTargetMatrixId(e.target.value)}
              className="sw-input"
            >
              {allMatrices.map((m) => (
                <option key={m.id} value={m.id}>{m.name} · {m.currency}</option>
              ))}
            </select>
            {targetMatrixId !== (matrixId || "main") && (
              <div className="text-[11px] text-[#7c5a1e] bg-[#fef8f2] border border-[#f5e8d5] rounded-[8px] px-2 py-1 mt-1">
                Vai mover pra outra matriz — moeda passa a ser {currency}.
              </div>
            )}
          </div>
          <div>
            <label className="sw-label">{t("sal.target_cell")}</label>
            <select data-testid="move-cell" value={cellId} onChange={(e) => setCellId(e.target.value)} className="sw-input">
              <option value="">{t("sal.select_cell")}</option>
              {(targetMatrix?.tracks || []).map((t) =>
                (targetMatrix?.levels || []).map((l) => {
                  const c = (targetMatrix?.cells || []).find((x) => x.track_id === t.id && x.level_id === l.id);
                  if (!c) return null;
                  return <option key={c.id} value={c.id}>{t.name} · {l.name} ({fmt(c.salary_min, currency)}–{fmt(c.salary_max, currency)})</option>;
                })
              )}
            </select>
            {cell && <div className="text-[10px] text-[#94a3b8] mt-1">{t("sal.band_hint", { min: fmt(cell.salary_min, currency), max: fmt(cell.salary_max, currency), pct: cell.target_bonus_pct })}</div>}
          </div>
          <div>
            <label className="sw-label">{t("sal.new_salary")} ({currency})</label>
            <input data-testid="move-salary" type="number" value={salary} onChange={(e) => setSalary(e.target.value)} className="sw-input" />
          </div>
          {outOfBand && (
            <label className="flex items-start gap-2 p-3 rounded-[10px] bg-rose-50 border border-rose-200 text-[12px] text-rose-800 cursor-pointer">
              <input data-testid="move-accept" type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5" />
              <span>{t("sal.out_of_band", { v: fmt(salary, currency), min: fmt(cell.salary_min, currency), max: fmt(cell.salary_max, currency) })}</span>
            </label>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="sw-label">{t("sal.effective_date")}</label>
              <input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="sw-input" />
            </div>
            <div>
              <label className="sw-label">Reason *</label>
              <input data-testid="move-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="sw-input" placeholder="Promotion / Annual raise / …" required />
            </div>
          </div>
        </div>

        {/* Inline salary history */}
        <div className="mt-5 border-t border-[#e2e8f0] pt-4" data-testid="move-history">
          <div className="flex items-center gap-2 mb-2">
            <History size={13} className="text-[#475569]" />
            <span className="text-[10.5px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{t("sal.salary_history")}</span>
          </div>
          {history === null && <div className="text-[12px] text-[#94a3b8]">{t("common.loading")}</div>}
          {history && history.length === 0 && <div className="text-[12px] text-[#94a3b8] italic">{t("sal.no_moves")}</div>}
          {history && history.length > 0 && (
            <div className="flex flex-col gap-2 max-h-[180px] overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="rounded-[10px] border border-[#e2e8f0] p-2.5">
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="font-semibold text-[#1e293b]">{fmt(h.from_salary, currency)} <ChevronRight size={11} className="inline mx-0.5" /> {fmt(h.to_salary, currency)}</span>
                    <span className="text-[10px] text-[#94a3b8]">{h.effective_date}</span>
                  </div>
                  <div className="text-[11.5px] text-[#475569] mt-0.5">{h.reason}</div>
                  {h.out_of_band && <span className="inline-block mt-1 text-[10px] text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full">out of band exception</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 mt-5 pt-4 border-t border-[#e2e8f0]">
          {currentCellId && (
            <button data-testid="move-remove" onClick={() => submit(true)} disabled={saving} className="text-[12px] text-rose-700 hover:bg-rose-50 px-2 py-1 rounded inline-flex items-center gap-1">
              <UserMinus2 size={12} /> Remove from matrix
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button
              data-testid="move-confirm"
              onClick={() => submit(false)}
              disabled={saving || !salary || !reason || (outOfBand && !accept)}
              className="sw-btn-primary"
            >
              <Move size={13} /> {saving ? "Saving…" : "Confirm move"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PendingSalaryRequests({ onChanged }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [comment, setComment] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/salary-move-requests?status=pending");
      setItems(r.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const decide = async (rid, decision) => {
    setBusyId(rid);
    try {
      await api.post(`/salary-move-requests/${rid}/decide`, { decision, comment });
      setComment("");
      await load();
      onChanged && onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Decide failed");
    } finally { setBusyId(null); }
  };

  if (items.length === 0) return null;
  return (
    <div data-testid="pending-salary-requests" className="sw-card border-amber-200 bg-amber-50">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={16} className="text-amber-700" />
        <h2 className="text-[14px] font-semibold text-amber-900">Pending salary movements · {items.length}</h2>
      </div>
      <div className="flex flex-col gap-3">
        {items.map((r) => {
          const nextRole = (r.required_roles || [])[r.current_step];
          const canDecide = user?.role === nextRole || user?.role === "admin";
          return (
            <div key={r.id} data-testid={`salary-req-${r.id}`} className="bg-white rounded-[12px] border border-amber-200 p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[13px] font-semibold text-[#1e293b]">{r.user_name}</div>
                  <div className="text-[12px] text-[#475569] mt-0.5">
                    {fmt(r.from_salary)} <ChevronRight size={11} className="inline mx-0.5" /> {fmt(r.to_salary)}
                    {r.bonus_pct != null && <span className="ml-2 text-[10px] text-[#94a3b8]">bonus {r.bonus_pct}%</span>}
                  </div>
                  <div className="text-[12px] text-[#475569] mt-1 italic">{r.reason}</div>
                  <div className="text-[10px] text-[#94a3b8] mt-1">Requested by {r.requested_by} · effective {r.effective_date}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-[#94a3b8]">Chain</div>
                  <div className="flex items-center gap-1 mt-1">
                    {(r.required_roles || []).map((role, idx) => (
                      <span key={idx} className={`text-[10px] px-2 py-0.5 rounded-full ${idx < r.current_step ? "bg-emerald-100 text-emerald-700" : idx === r.current_step ? "bg-amber-200 text-amber-900 font-semibold" : "bg-slate-100 text-slate-500"}`}>{role}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-amber-200/60 flex items-center gap-2 flex-wrap">
                {canDecide ? (
                  <>
                    <input
                      data-testid={`salary-req-comment-${r.id}`}
                      placeholder="Optional comment…"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="sw-input !py-1 !text-[12px] flex-1 min-w-[160px]"
                    />
                    <button data-testid={`salary-req-approve-${r.id}`} disabled={busyId === r.id} onClick={() => decide(r.id, "approved")} className="sw-btn-primary !py-1 !text-[12px]">{t("common.approve")}</button>
                    <button data-testid={`salary-req-reject-${r.id}`} disabled={busyId === r.id} onClick={() => decide(r.id, "rejected")} className="sw-btn-secondary !py-1 !text-[12px] !text-rose-700">{t("common.reject")}</button>
                  </>
                ) : (
                  <span className="text-[11px] text-[#94a3b8] italic">{t("sal.awaiting")} <strong className="text-[#475569]">{nextRole}</strong>…</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HistoryModal({ user, onClose }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/users/${user.id}/salary-history`).then((r) => setItems(r.data)).catch(() => {});
  }, [user.id]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="history-modal" className="bg-white rounded-[20px] max-w-[520px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="sw-heading text-[18px]">Salary history</h3>
            <p className="text-[12px] text-[#747474] mt-1">{user.name}</p>
          </div>
          <button onClick={onClose} className="text-[#94a3b8]"><X size={20} /></button>
        </div>
        {items.length === 0 ? (
          <div className="text-[12px] text-[#94a3b8] text-center py-8">{t("sal.no_history")}</div>
        ) : (
          <div className="flex flex-col gap-3">
            {items.map((h) => (
              <div key={h.id} className="rounded-[12px] border border-[#e2e8f0] p-3">
                <div className="flex items-center justify-between">
                  <div className="text-[13px] font-semibold text-[#1e293b]">
                    {fmt(h.from_salary)} <ChevronRight size={12} className="inline mx-1" /> {fmt(h.to_salary)}
                  </div>
                  <span className="text-[10px] text-[#94a3b8]">{h.effective_date}</span>
                </div>
                <div className="text-[12px] text-[#475569] mt-1">{h.reason}</div>
                {h.out_of_band && <span className="inline-block mt-1 text-[10px] text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full">out of band exception</span>}
                {h.notes && <div className="text-[11px] text-[#94a3b8] italic mt-1">{h.notes}</div>}
                <div className="text-[10px] text-[#94a3b8] mt-1">by {h.recorded_by}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Iter 47d — Allocation list. Replaces the old "Unplaced team members" strip.
 *
 * - Fetches /salary-allocations (already enriched with matrix/cell/salary/dept names).
 * - Groups rows by department (expandable tree). Search box + role/dept/matrix chip filters.
 * - Each row shows: name · role · current matrix + cell (track · level) · current salary · Move button.
 * - When `cellFilterId` is set (parent clicked a matrix cell), auto-filters and shows a
 *   dismissable chip at the top.
 */
function EmployeeAllocationList({ cellFilterId, matrixId, currency, cellsById, tracksById, levelsById, onClearCellFilter, onMove, refreshKey }) {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [placementFilter, setPlacementFilter] = useState("all"); // all | placed | unplaced
  const [openDepts, setOpenDepts] = useState({});

  const load = () => {
    api.get("/salary-allocations").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [refreshKey]); // reload when parent matrix changes

  const deptsInList = useMemo(() => {
    const uniq = new Map();
    rows.forEach((r) => uniq.set(r.department_id || "__none__", r.department_name || "Sem departamento"));
    return Array.from(uniq.entries()).sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  }, [rows]);

  // Cell-filter chip label
  const cellFilterLabel = useMemo(() => {
    if (!cellFilterId) return null;
    const c = cellsById[cellFilterId];
    if (!c) return "célula selecionada";
    const t = tracksById[c.track_id];
    const l = levelsById[c.level_id];
    return `${t?.name || "?"} · ${l?.name || "?"}`;
  }, [cellFilterId, cellsById, tracksById, levelsById]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (cellFilterId && r.salary_cell_id !== cellFilterId) return false;
      if (roleFilter !== "all" && r.role !== roleFilter) return false;
      if (deptFilter !== "all") {
        const key = r.department_id || "__none__";
        if (key !== deptFilter) return false;
      }
      if (placementFilter === "placed" && r.unplaced) return false;
      if (placementFilter === "unplaced" && !r.unplaced) return false;
      if (!needle) return true;
      const hay = [r.name, r.job_title, r.role, r.matrix_name, r.cell_track_name, r.cell_level_name].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, roleFilter, deptFilter, placementFilter, cellFilterId]);

  const rowsByDept = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      const key = r.department_id || "__none__";
      m[key] = m[key] || { name: r.department_name || "Sem departamento", items: [] };
      m[key].items.push(r);
    });
    Object.values(m).forEach((v) => v.items.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    return m;
  }, [filtered]);

  // Auto-expand every dept when a search/filter is active so users immediately see matches
  useEffect(() => {
    if (q.trim() || cellFilterId || roleFilter !== "all" || placementFilter !== "all") {
      const next = {};
      Object.keys(rowsByDept).forEach((k) => { next[k] = true; });
      setOpenDepts(next);
    }
  }, [q, cellFilterId, roleFilter, placementFilter, rowsByDept]);

  const roles = ["employee", "manager", "hr", "ceo"];
  const orderedDeptKeys = Object.keys(rowsByDept).sort((a, b) => (rowsByDept[a].name || "").localeCompare(rowsByDept[b].name || ""));

  return (
    <div className="sw-card !p-4" data-testid="allocation-list">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <UsersIcon size={16} className="text-[#475569]" />
          <h2 className="sw-heading text-[16px]">{t("sal.employees_count", { a: filtered.length, b: rows.length })}</h2>
        </div>
        {cellFilterId && (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#eef2f7] border border-[#475569] text-[11.5px] text-[#1e293b]" data-testid="cell-filter-chip">
            Filtro por célula: <b>{cellFilterLabel}</b>
            <button type="button" onClick={onClearCellFilter} className="text-[#475569] hover:text-[#0f172a]" data-testid="clear-cell-filter"><X size={12} /></button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("sal.search_ph")} className="sw-input !pl-8 !text-[12.5px]" data-testid="alloc-search" />
        </div>
        <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} className="sw-input !w-auto !text-[12.5px]" data-testid="alloc-dept-filter">
          <option value="all">{t("admin.all_depts")}</option>
          {deptsInList.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="sw-input !w-auto !text-[12.5px]" data-testid="alloc-role-filter">
          <option value="all">{t("sal.all_roles")}</option>
          {roles.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
        </select>
        <select value={placementFilter} onChange={(e) => setPlacementFilter(e.target.value)} className="sw-input !w-auto !text-[12.5px]" data-testid="alloc-placement-filter">
          <option value="all">{t("common.all")}</option>
          <option value="placed">{t("sal.only_allocated")}</option>
          <option value="unplaced">{t("sal.only_unallocated")}</option>
        </select>
      </div>

      {/* Tree */}
      <div className="border border-[#eeeded] rounded-[12px] max-h-[520px] overflow-y-auto bg-white">
        {orderedDeptKeys.length === 0 && (
          <div className="text-center py-8 text-[12.5px] text-[#94a3b8] italic">{t("sal.no_emp_filters")}</div>
        )}
        {orderedDeptKeys.map((key) => {
          const bucket = rowsByDept[key];
          const isOpen = openDepts[key];
          return (
            <div key={key} className="border-b border-[#f1f5f9] last:border-b-0">
              <button
                type="button"
                onClick={() => setOpenDepts({ ...openDepts, [key]: !isOpen })}
                className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#f8fafc]"
                data-testid={`alloc-dept-toggle-${key}`}
              >
                {isOpen ? <ChevronDown size={13} className="text-[#475569]" /> : <ChevronRight size={13} className="text-[#475569]" />}
                <span className="text-[13px] font-semibold text-[#0f172a] flex-1 text-left">{bucket.name}</span>
                <span className="text-[11px] text-[#94a3b8]">{t("sal.people_n", { n: bucket.items.length })}</span>
              </button>
              {isOpen && (
                <div className="divide-y divide-[#f1f5f9]">
                  {bucket.items.map((r) => (
                    <div key={r.user_id} data-testid={`alloc-row-${r.user_id}`} className="flex items-center gap-3 pl-10 pr-3 py-2 hover:bg-[#fbfbfd]">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] font-medium text-[#0f172a] truncate">{r.name}</span>
                          <span className="text-[10.5px] px-1.5 py-0.5 rounded-full bg-[#eef2f7] text-[#475569] capitalize">{r.role}</span>
                          {r.job_title && <span className="text-[11px] text-[#94a3b8] truncate">· {r.job_title}</span>}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mt-0.5 text-[11px]">
                          {r.unplaced ? (
                            <span className="text-[#7c5a1e] bg-[#fef8f2] border border-[#f5e8d5] px-2 py-0.5 rounded-full">{t("sal.unallocated")}</span>
                          ) : (
                            <>
                              <span className="text-[#475569]">{r.matrix_name || "?"}</span>
                              <span className="text-[#94a3b8]">·</span>
                              <span className="text-[#475569]">{r.cell_track_name} · {r.cell_level_name}</span>
                              <span className="text-[#94a3b8]">·</span>
                              <span className="text-[#0f172a] font-semibold">{fmt(r.current_salary, r.currency || "USD")}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        data-testid={`alloc-move-${r.user_id}`}
                        onClick={() => onMove({
                          id: r.user_id, name: r.name, role: r.role, job_title: r.job_title, current_salary: r.current_salary,
                        }, r.salary_cell_id)}
                        className="sw-btn-secondary !text-[11.5px]"
                      ><Move size={11} /> Mover</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}